import React, { useState } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import Button from "@material-ui/core/Button";
import Collapse from "@material-ui/core/Collapse";

const useStyles = makeStyles(() => ({
	card: {
		marginTop: 8,
		padding: 10,
		display: "flex",
		flexDirection: "column",
		gap: 6,
		cursor: "pointer",
	},
	topRow: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "flex-start",
		gap: 8,
	},
	headingText: {
		flex: 1,
		minWidth: 0,
	},
	badgesRow: {
		display: "flex",
		gap: 6,
		flexWrap: "wrap",
		marginTop: 4,
	},
	valuesRow: {
		display: "flex",
		flexDirection: "column",
		gap: 1,
		marginTop: 2,
		padding: 6,
		borderRadius: 6,
		background: "#f1f5f9",
	},
	orderValue: {
		fontWeight: 700,
		color: "#0f172a",
	},
	details: {
		marginTop: 6,
		paddingTop: 6,
		borderTop: "1px solid rgba(0, 0, 0, 0.08)",
		display: "flex",
		flexDirection: "column",
		gap: 2,
	},
	action: {
		marginTop: 6,
		alignSelf: "flex-start",
	},
}));

// Mirrors the mobile CertificateStatusBadge palette (App.js):
// aprovado -> verde claro, aguardando -> ambar claro, demais -> cinza.
const statusBackground = status => {
	const normalized = String(status || "").toLowerCase();
	if (normalized.includes("aprov")) {
		return "#dcfce7";
	}
	if (normalized.includes("aguard")) {
		return "#fef3c7";
	}
	return "#e2e8f0";
};

const formatMoney = value => {
	const normalized = String(value || "").trim();
	if (!normalized) {
		return "-";
	}
	return /r\$/i.test(normalized) ? normalized : `R$ ${normalized}`;
};

const CertificateStatusBadge = ({ status }) => (
	<Chip
		size="small"
		label={status || "Sem status"}
		style={{ backgroundColor: statusBackground(status), color: "#1e293b", fontWeight: 600 }}
	/>
);

const PaymentStatusBadge = ({ paymentStatus }) => {
	const normalized = String(paymentStatus || "").trim().toLowerCase();
	if (!normalized) {
		return null;
	}
	if (normalized.includes("a receber")) {
		return <Chip size="small" label="🕒 A receber" style={{ backgroundColor: "#f59e0b", color: "#fff" }} />;
	}
	if (normalized.includes("conciliad")) {
		return <Chip size="small" label="🟢 Conciliado" style={{ backgroundColor: "#16a34a", color: "#fff" }} />;
	}
	return null;
};

const DeliveryStatusBadge = ({ paymentSentStatus, serviceSentStatus, approvalSentStatus }) => {
	const payment = String(paymentSentStatus || "").trim().toLowerCase();
	const service = String(serviceSentStatus || "").trim().toLowerCase();
	const approval = String(approvalSentStatus || "").trim().toLowerCase();

	const sent = label => (
		<Chip size="small" label={label} style={{ backgroundColor: "#16a34a", color: "#fff" }} />
	);
	const failed = label => (
		<Chip size="small" label={label} style={{ backgroundColor: "#b91c1c", color: "#fff" }} />
	);

	if (approval === "sim") {
		return sent("🟢 Aprovação enviada");
	}
	if (approval.includes("erro")) {
		return failed("✖ Erro ao enviar a aprovação");
	}
	if (service === "sim") {
		return sent("🟢 Atendimento enviado");
	}
	if (service.includes("erro")) {
		return failed("✖ Erro ao enviar o atendimento");
	}
	if (payment === "sim") {
		return sent("🟢 Pagamento enviado");
	}
	if (payment.includes("erro")) {
		return failed("✖ Erro ao enviar o pagamento");
	}
	return null;
};

const DetailLine = ({ label, value }) => (
	<Typography variant="caption" color="textSecondary">
		{label}: {value || "-"}
	</Typography>
);

const CertificateOrderCard = ({ order, actionLabel, onAction, actionLoading }) => {
	const classes = useStyles();
	const [expanded, setExpanded] = useState(false);

	if (!order) {
		return null;
	}

	return (
		<Paper variant="outlined" className={classes.card} onClick={() => setExpanded(current => !current)}>
			<div className={classes.topRow}>
				<div className={classes.headingText}>
					<Typography variant="subtitle2" noWrap>
						{order.customerName || "Sem nome"} #{order.identifier || "-"}
					</Typography>
					<Typography variant="body2" color="textSecondary">
						{order.productName || "Produto não informado"}
					</Typography>
				</div>
				<CertificateStatusBadge status={order.status} />
			</div>

			<div className={classes.badgesRow}>
				{order.protocol ? (
					<Chip
						size="small"
						label={`Protocolo ${order.protocol}`}
						style={{ backgroundColor: "#3f51b5", color: "#fff" }}
					/>
				) : null}
				<PaymentStatusBadge paymentStatus={order.paymentStatus} />
				<DeliveryStatusBadge
					paymentSentStatus={order.paymentSentStatus}
					serviceSentStatus={order.serviceSentStatus}
					approvalSentStatus={order.approvalSentStatus}
				/>
			</div>

			<Typography variant="body2" color="textSecondary">
				{(order.dateLabel || "-") + " • " + (order.vendor || "Sem vendedor")}
			</Typography>

			{order.orderValue || order.productSuggestedValue ? (
				<div className={classes.valuesRow}>
					<Typography variant="body2" className={classes.orderValue}>
						Valor deste pedido: {formatMoney(order.orderValue)}
					</Typography>
					<Typography variant="caption" color="textSecondary">
						Valor sugerido do produto: {formatMoney(order.productSuggestedValue)}
					</Typography>
				</div>
			) : null}

			<Collapse in={expanded} unmountOnExit>
				<div className={classes.details}>
					<DetailLine label="Documento" value={order.document} />
					<DetailLine label="Telefone" value={order.phone} />
					<DetailLine label="Email" value={order.email} />
					<DetailLine label="Contabilidade" value={order.accounting} />
					<DetailLine label="Unidade" value={order.unitName || order.unitCode} />
					<DetailLine label="Aprovação" value={order.approvalDate} />
					<DetailLine label="Indicação" value={order.indicationRaw} />
					<DetailLine label="Stage" value={order.stage} />
				</div>
			</Collapse>

			{onAction ? (
				<Button
					variant="contained"
					color="primary"
					size="small"
					className={classes.action}
					disabled={actionLoading}
					onClick={event => {
						event.stopPropagation();
						onAction(order);
					}}
				>
					{actionLoading ? "Aplicando..." : actionLabel || "Usar pedido"}
				</Button>
			) : null}
		</Paper>
	);
};

export default CertificateOrderCard;
